'use client'

import { useMemo } from 'react'
import { usePathname } from 'next/navigation'

export type SamanthaPageContext =
  | {
      kind: 'default'
      pathname: string
      canPublish: false
      placeholder: string
      label: string
    }
  | {
      kind: 'space'
      pathname: string
      spaceId: number
      canPublish: true
      placeholder: string
      label: string
    }
  | {
      kind: 'encounter'
      pathname: string
      spaceId: number
      encounterId: number
      canPublish: true
      placeholder: string
      label: string
    }
  | {
      kind: 'course'
      pathname: string
      courseId: number | null
      lessonId: number | null
      canPublish: false
      placeholder: string
      label: string
    }

function parsePositiveInt(
  value?: string | null
) {
  const parsed =
    Number(value || 0)

  return Number.isInteger(parsed) &&
    parsed > 0
    ? parsed
    : null
}

export function resolveSamanthaPageContext(
  pathname?: string | null
): SamanthaPageContext {
  const safePathname =
    String(
      pathname || ''
    ).trim()

  /*
   * Encounter
   *
   * /spaces/334/encounters/81
   */
  const encounterMatch =
    safePathname.match(
      /^\/spaces\/(\d+)\/encounters\/(\d+)(?:\/|$)/
    )

  if (encounterMatch) {
    const spaceId =
      parsePositiveInt(
        encounterMatch[1]
      )

    const encounterId =
      parsePositiveInt(
        encounterMatch[2]
      )

    if (
      spaceId &&
      encounterId
    ) {
      return {
        kind: 'encounter',
        pathname:
          safePathname,
        spaceId,
        encounterId,
        canPublish: true,
        placeholder:
          'Aporta a este Encuentro o pregúntale a Samantha…',
        label:
          'Encuentro',
      }
    }
  }

  /*
   * Space
   *
   * /spaces/334
   */
  const spaceMatch =
    safePathname.match(
      /^\/spaces\/(\d+)(?:\/|$)/
    )

  if (spaceMatch) {
    const spaceId =
      parsePositiveInt(
        spaceMatch[1]
      )

    if (spaceId) {
      return {
        kind: 'space',
        pathname:
          safePathname,
        spaceId,
        canPublish: true,
        placeholder:
          'Comparte algo aquí o pregúntale a Samantha…',
        label:
          'Space',
      }
    }
  }

  /*
   * Course
   *
   * Soportamos rutas variables sin asumir
   * todavía una estructura exacta.
   *
   * /courses/123
   * /courses/123/lessons/456
   * /courses/123/lesson/456
   */
  if (
    safePathname ===
      '/courses' ||
    safePathname.startsWith(
      '/courses/'
    )
  ) {
    const parts =
      safePathname
        .split('/')
        .filter(Boolean)

    const courseId =
      parsePositiveInt(
        parts[1]
      )

    let lessonId:
      number | null =
      null

    const lessonIndex =
      parts.findIndex(
        part =>
          part ===
            'lesson' ||
          part ===
            'lessons'
      )

    if (
      lessonIndex >= 0
    ) {
      lessonId =
        parsePositiveInt(
          parts[
            lessonIndex + 1
          ]
        )
    }

    return {
      kind: 'course',
      pathname:
        safePathname,
      courseId,
      lessonId,
      canPublish: false,
      placeholder:
        'Pregúntale a Samantha sobre este contenido…',
      label:
        'Curso',
    }
  }

  /*
   * Default
   */
  return {
    kind: 'default',
    pathname:
      safePathname,
    canPublish: false,
    placeholder:
      '¿Qué quieres hacer?',
    label:
      'Samantha',
  }
}

export function useSamanthaPageContext() {
  const pathname =
    usePathname()

  return useMemo(
    () =>
      resolveSamanthaPageContext(
        pathname
      ),
    [
      pathname,
    ]
  )
}